'use client';

import { useRef, useState } from 'react';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { SearchApiRatingCommentsOrderBy } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { useToast } from '@/components/ui/use-toast';
import { getLogger, type PlainValue } from '@/logger/logger';
import { RatingComment } from '@/types';
import { AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';

export const COMMENTS_SORT_ERROR_MESSAGE = 'Erro ao ordenar os comentários.';

export const COMMENTS_SORT_ANALYTICS_CRITERIA: Record<SearchApiRatingCommentsOrderBy, string> = {
  [SearchApiRatingCommentsOrderBy.NEWEST]: 'mais_recentes',
  [SearchApiRatingCommentsOrderBy.OLDEST]: 'mais_antigos',
  [SearchApiRatingCommentsOrderBy.HIGHEST_RATING]: 'maior_avaliacao',
  [SearchApiRatingCommentsOrderBy.LOWEST_RATING]: 'menor_avaliacao',
};

export interface EntityCommentsPage {
  comments: RatingComment[];
  hasMore: boolean;
}

export type LoadEntityCommentsPage = (
  page: number,
  orderBy: SearchApiRatingCommentsOrderBy,
) => Promise<EntityCommentsPage>;

interface SortedCommentsPage extends EntityCommentsPage {
  orderBy: SearchApiRatingCommentsOrderBy;
}

export interface EntityCommentsLogger {
  context: string;
  entity: Record<string, PlainValue>;
}

export interface UseEntityCommentsReturn {
  orderBy: SearchApiRatingCommentsOrderBy;
  comments: RatingComment[];
  hasMore: boolean;
  commentsKey: string;
  isSorting: boolean;
  changeOrderBy: (next: SearchApiRatingCommentsOrderBy) => void;
}

export function useEntityComments(
  loadPage: LoadEntityCommentsPage,
  initialComments: RatingComment[],
  initialHasMore: boolean,
  logger: EntityCommentsLogger,
  analyticsEvent: AnalyticsEnumKeyType,
): UseEntityCommentsReturn {
  const { toast } = useToast();
  const [orderBy, setOrderBy] = useState(SearchApiRatingCommentsOrderBy.NEWEST);
  const [sortedPage, setSortedPage] = useState<SortedCommentsPage | null>(null);
  const [isSorting, setIsSorting] = useState(false);
  const requestRef = useRef(0);
  const orderByRef = useRef(orderBy);
  const appliedOrderByRef = useRef(orderBy);

  const changeOrderBy = (next: SearchApiRatingCommentsOrderBy) => {
    if (next === orderByRef.current) {
      return;
    }

    getAnalyticsPlugin().track(analyticsEvent, {
      criterio: COMMENTS_SORT_ANALYTICS_CRITERIA[next],
      criterio_anterior: COMMENTS_SORT_ANALYTICS_CRITERIA[orderByRef.current],
    });

    orderByRef.current = next;
    setOrderBy(next);
    setIsSorting(true);

    requestRef.current += 1;
    const request = requestRef.current;

    loadPage(1, next)
      .then(({ comments, hasMore }) => {
        if (request !== requestRef.current) {
          return;
        }

        appliedOrderByRef.current = next;
        setSortedPage({ orderBy: next, comments, hasMore });
        setIsSorting(false);
      })
      .catch((error) => {
        if (request !== requestRef.current) {
          return;
        }

        getLogger(logger.context).error('Failed to sort the entity comments', {
          entity: { event: 'comments_sort_failed', ...logger.entity, order_by: next },
          error,
        });

        orderByRef.current = appliedOrderByRef.current;
        setOrderBy(appliedOrderByRef.current);
        setIsSorting(false);
        toast.error(COMMENTS_SORT_ERROR_MESSAGE);
      });
  };

  return {
    orderBy,
    comments: sortedPage?.comments ?? initialComments,
    hasMore: sortedPage?.hasMore ?? initialHasMore,
    commentsKey: sortedPage?.orderBy ?? SearchApiRatingCommentsOrderBy.NEWEST,
    isSorting,
    changeOrderBy,
  };
}
